import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { TrendingUp } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import SynopsisSectionCard from "./SynopsisSectionCard";
import DropdownSelector from "../../../Components/DropdownSelector";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import { fetchAssessmentTrendline } from "../../../Store/Reducers/AccountWorkspaceSlice";
import { fetchOrganizationLevels } from "../../../Store/Reducers/AdminSlice";
import { fetchSelfAssessmentConfig } from "../../../Store/Reducers/AssessmentSlice";
import {
  isAccountTeamRole,
  isRole,
  ROLES,
} from "../../../Utils/Constants/RoleConstants";
import { parseLevelNumber } from "../../../Utils/Helper/levelUtils";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../../Utils/Helper/LocalStorage";
import {
  DEFAULT_CYCLE_CURRENCY_DAYS,
  resolveSelfAssessmentCurrencyDays,
} from "../../../Utils/Helper/selfAssessmentCurrency";

type ChartPoint = {
  name: string;
  /** Level value (Unknown=-1, or carried last-known level for trailing months). */
  value: number | null;
  /**
   * Continuous X so each month owns an equal-width column (tick at monthIndex).
   * Multiple assessments in a month cluster tightly around that tick.
   */
  x: number;
  /** 0-based month bucket index within the selected period. */
  monthIndex: number;
  /** Unique key so duplicate month labels still plot as separate points. */
  key: string;
  publishedDate?: string;
  levelCode?: string;
  assessmentName?: string;
  /** Change vs previous point; null for the first point. */
  trendDelta?: number | null;
  /** True when no assessment data exists for this month bucket. */
  isUnknown?: boolean;
  /**
   * Trailing month after the last known assessment — hold the last known level
   * with a dotted line (no drop to Unknown).
   */
  isTrailingEmpty?: boolean;
  /** Yellow bubble once published + currency window has elapsed (through today). */
  isStaleDot?: boolean;
  /** Carry endpoint at today's date. */
  isToday?: boolean;
  /** Month label only (e.g. next month) — not plotted on the line. */
  isAxisOnly?: boolean;
};

/**
 * Place point(s) in a month's equal-width slot.
 * Month i is centered at x=i (same as the month tick). Multiple assessments
 * stay in a tight cluster around that center so the UI matches single-point months.
 * First month is clamped to x>=0 so it lines up with the Unknown Y-axis origin.
 */
const monthSlotX = (
  monthIndex: number,
  indexInMonth: number,
  countInMonth: number
): number => {
  if (countInMonth <= 1) return monthIndex;
  const clusterWidth = Math.min(0.2, 0.08 * (countInMonth - 1));
  let start = monthIndex - clusterWidth / 2;
  if (start < 0) start = 0;
  return start + (clusterWidth * indexInMonth) / (countInMonth - 1);
};

type TrendPeriod = "3m" | "6m";

type AssessmentsTrendsSectionProps = {
  accountId: string | undefined;
  onOpenAssessments: () => void;
  /** When true, renders header + body without the outer card chrome. */
  embedded?: boolean;
};

const AL_STROKE = "#14b8a6";
const RL_STROKE = "#60a5fa";
const CHART_HEIGHT = 190;

/** Sentinel level plotted below L0 for months with no assessment data. */
const UNKNOWN_LEVEL = -1;
const UNKNOWN_COLOR = "#9ca3af";

const PERIOD_OPTIONS: { label: string; value: TrendPeriod }[] = [
  { label: "3 months", value: "3m" },
  { label: "6 months", value: "6m" },
];

const STALE_COLOR = "#eab308";

type StaleAssessmentCta = {
  label: string;
  path: string;
};

const resolveStaleAssessmentCta = (
  withOrg: (path: string) => string
): StaleAssessmentCta | null => {
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole =
    loggedInUser?.role ||
    loggedInUser?.user?.role ||
    loggedInUser?.user_role ||
    null;

  if (
    isRole(userRole, ROLES.VENDOR_ADMIN) ||
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD)
  ) {
    return {
      label: "Go to Assessments Hub",
      path: withOrg("/ops?tab=assessments"),
    };
  }

  if (
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR) ||
    isRole(userRole, ROLES.EXECUTIVE) ||
    isRole(userRole, ROLES.C_LEVEL) ||
    isAccountTeamRole(userRole)
  ) {
    return {
      label: "Go to My Assessments",
      path: withOrg("/pending-assessments"),
    };
  }

  return null;
};

const parseChartDate = (raw?: string): Date | null => {
  if (!raw) return null;
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number): Date => {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatISODate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Fallback L0–L5 ticks for sample preview charts. */
const DEFAULT_LEVEL_TICKS = [0, 1, 2, 3, 4, 5];

const LEVEL_LABEL_COLORS: Record<number, string> = {
  0: "#94a3b8",
  1: "#ef4444",
  2: "#f97316",
  3: "#eab308",
  4: "#22c55e",
  5: "#16a34a",
};

const getTrendDateRange = (
  period: TrendPeriod
): { from_date: string; to_date: string } => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - (period === "3m" ? 3 : 6));
  const format = (d: Date) => d.toISOString().slice(0, 10);
  return { from_date: format(from), to_date: format(to) };
};

/** AL trend axis is always exactly L0–L5, regardless of higher levels an org may have configured. */
const buildContinuousLevelTicks = (): number[] =>
  Array.from({ length: 6 }, (_, i) => i);

const buildLevelCodeByNumber = (
  organizationLevels: Array<{ level_code: string }>
): Record<number, string> => {
  const map: Record<number, string> = {};
  organizationLevels.forEach(level => {
    const n = parseLevelNumber(level.level_code);
    if (Number.isFinite(n) && !map[n]) {
      map[n] = level.level_code;
    }
  });
  return map;
};

const formatTrendTooltipDate = (dateStr: string | undefined): string | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Short label above the yellow stale marker (e.g. "Aug 11"). */
const formatStaleAxisDate = (dateStr: string | undefined): string | null => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
  });
};

type TrendMonthBucket = {
  year: number;
  month: number;
  label: string;
  key: string;
};

const dateToMonthX = (
  date: Date,
  buckets: TrendMonthBucket[]
): number | null => {
  const idx = buckets.findIndex(
    bucket =>
      bucket.year === date.getFullYear() && bucket.month === date.getMonth()
  );
  if (idx < 0) return null;
  const daysInMonth = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0
  ).getDate();
  return idx + (date.getDate() - 1) / daysInMonth;
};

/** Trailing month buckets (oldest → newest), always including next month. */
const buildTrendMonthBuckets = (period: TrendPeriod): TrendMonthBucket[] => {
  const count = period === "3m" ? 3 : 6;
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - (count - 1), 1);
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const end = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);

  const buckets: TrendMonthBucket[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    buckets.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      label: cursor.toLocaleString("en-US", { month: "short" }),
      key: `bucket-${cursor.getFullYear()}-${cursor.getMonth()}`,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return buckets;
};

/**
 * Plots every assessment in each month bucket (same-month multiples included).
 * Each month gets an equal-width X slot; multiples share that slot.
 * Empty months before/between known data become Unknown. After the last known
 * assessment, later months keep the last known level (dotted carry-forward line).
 */
const buildAlChartPoints = (
  buckets: TrendMonthBucket[],
  points: {
    published_date: string;
    level_number: number;
    level?: string;
    assessment_name?: string;
  }[],
  levelCodeByNumber: Record<number, string>,
  currencyDays: number
): ChartPoint[] => {
  const sorted = [...points].sort((a, b) => {
    const da = parseChartDate(a.published_date)?.getTime() ?? 0;
    const db = parseChartDate(b.published_date)?.getTime() ?? 0;
    return da - db;
  });

  let prevKnownValue: number | null = null;

  const chartPoints: ChartPoint[] = buckets.flatMap(
    (bucket, monthIndex): ChartPoint[] => {
      const matches = sorted.filter(point => {
        const d = parseChartDate(point.published_date);
        return (
          !!d &&
          d.getFullYear() === bucket.year &&
          d.getMonth() === bucket.month
        );
      });

      if (!matches.length) {
        return [
          {
            key: bucket.key,
            name: bucket.label,
            value: UNKNOWN_LEVEL,
            x: monthSlotX(monthIndex, 0, 1),
            monthIndex,
            isUnknown: true,
            trendDelta: null,
          },
        ];
      }

      return matches.map((point, index): ChartPoint => {
        const publishedDate = parseChartDate(point.published_date);
        const sameDayIndex = matches
          .slice(0, index)
          .filter(
            other =>
              parseChartDate(other.published_date)?.getTime() ===
              publishedDate?.getTime()
          ).length;
        const publishedX =
          publishedDate != null ? dateToMonthX(publishedDate, buckets) : null;
        const baseX =
          publishedX ?? monthSlotX(monthIndex, index, matches.length);
        const trendDelta =
          prevKnownValue != null ? point.level_number - prevKnownValue : null;
        prevKnownValue = point.level_number;

        return {
          key: `${bucket.key}-${point.published_date}-${index}`,
          name: bucket.label,
          value: point.level_number,
          x: baseX + sameDayIndex * 0.03,
          monthIndex,
          publishedDate: point.published_date,
          levelCode:
            point.level ??
            levelCodeByNumber[point.level_number] ??
            `L${point.level_number}`,
          assessmentName: point.assessment_name,
          isUnknown: false,
          trendDelta,
        };
      });
    }
  );

  let lastKnownIndex = -1;
  for (let i = chartPoints.length - 1; i >= 0; i--) {
    if (!chartPoints[i].isUnknown) {
      lastKnownIndex = i;
      break;
    }
  }

  // No real data — keep Unknown placeholders (sample overlay handles empty).
  if (lastKnownIndex < 0) return chartPoints;

  const lastKnown = chartPoints[lastKnownIndex];
  const carryValue = lastKnown.value;
  if (carryValue == null) return chartPoints;

  const lastKnownDate = parseChartDate(lastKnown.publishedDate);
  const today = startOfDay(new Date());
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const carried: ChartPoint[] = [];
  chartPoints.forEach((point, index) => {
    if (index <= lastKnownIndex || !point.isUnknown) {
      carried.push(point);
      return;
    }

    const bucket = buckets[point.monthIndex];
    if (!bucket) return;
    const bucketStart = new Date(bucket.year, bucket.month, 1);

    // Next month (e.g. Sep): keep the axis label, do not plot past today.
    if (bucketStart > currentMonthStart) {
      carried.push({
        key: point.key,
        name: point.name,
        value: null,
        x: point.x,
        monthIndex: point.monthIndex,
        isAxisOnly: true,
        trendDelta: null,
      });
      return;
    }

    // Current month is capped at today — skip the month-start placeholder.
    if (
      bucket.year === today.getFullYear() &&
      bucket.month === today.getMonth()
    ) {
      return;
    }

    carried.push({
      key: point.key,
      name: point.name,
      value: carryValue,
      x: point.x,
      monthIndex: point.monthIndex,
      levelCode: lastKnown.levelCode,
      publishedDate: lastKnown.publishedDate,
      isUnknown: false,
      isTrailingEmpty: true,
      trendDelta: null,
    });
  });

  if (!lastKnownDate) return carried;

  const extras: ChartPoint[] = [];

  const staleOn = addDays(lastKnownDate, currencyDays);
  const staleX = dateToMonthX(staleOn, buckets);
  const staleMonthIndex = buckets.findIndex(
    bucket =>
      bucket.year === staleOn.getFullYear() &&
      bucket.month === staleOn.getMonth()
  );
  if (
    staleOn <= today &&
    staleX != null &&
    staleMonthIndex >= 0 &&
    staleOn.getTime() !== lastKnownDate.getTime()
  ) {
    extras.push({
      key: "stale-currency",
      name: buckets[staleMonthIndex].label,
      value: carryValue,
      x: staleX,
      monthIndex: staleMonthIndex,
      levelCode: lastKnown.levelCode,
      publishedDate: formatISODate(staleOn),
      isUnknown: false,
      isTrailingEmpty: true,
      isStaleDot: true,
      trendDelta: null,
    });
  }

  const todayX = dateToMonthX(today, buckets);
  const todayMonthIndex = buckets.findIndex(
    bucket =>
      bucket.year === today.getFullYear() && bucket.month === today.getMonth()
  );
  const staleOverlapsToday =
    staleX != null && todayX != null && Math.abs(staleX - todayX) < 0.05;
  if (
    todayX != null &&
    todayMonthIndex >= 0 &&
    lastKnownDate < today &&
    !staleOverlapsToday
  ) {
    extras.push({
      key: "carry-today",
      name: buckets[todayMonthIndex].label,
      value: carryValue,
      x: todayX,
      monthIndex: todayMonthIndex,
      levelCode: lastKnown.levelCode,
      publishedDate: formatISODate(today),
      isUnknown: false,
      isTrailingEmpty: true,
      isToday: true,
      trendDelta: null,
    });
  }

  if (!extras.length) return carried;

  return [
    ...carried.filter(
      point =>
        !extras.some(
          extra =>
            (point.isTrailingEmpty || point.isAxisOnly) &&
            Math.abs(point.x - extra.x) < 0.05
        )
    ),
    ...extras,
  ].sort((a, b) => a.x - b.x);
};

const buildSamplePoints = (
  levelTicks: number[],
  period: TrendPeriod
): ChartPoint[] => {
  const ticks = levelTicks.length ? levelTicks : DEFAULT_LEVEL_TICKS;
  const midLow = ticks[Math.min(1, ticks.length - 1)] ?? 1;
  const mid = ticks[Math.min(2, ticks.length - 1)] ?? 2;
  const midHigh = ticks[Math.min(3, ticks.length - 1)] ?? 3;
  const count = period === "3m" ? 3 : 6;
  const history =
    count === 3
      ? [midLow, mid, midHigh]
      : [midLow, midLow, mid, mid, midHigh, midHigh];
  const seed = [...history, history[history.length - 1]];
  const now = new Date();
  return seed.map((value, index) => {
    const d = new Date(
      now.getFullYear(),
      now.getMonth() - (count - 1 - index),
      1
    );
    return {
      key: `sample-${index}`,
      name: d.toLocaleString("en-US", { month: "short" }),
      value,
      x: monthSlotX(index, 0, 1),
      monthIndex: index,
      publishedDate: d.toISOString().slice(0, 10),
      levelCode: `L${value}`,
      trendDelta: index > 0 ? value - seed[index - 1] : null,
    };
  });
};

type TrendTooltipContentProps = TooltipProps<number, string> & {
  prefix: "AL" | "RL";
  currencyDays?: number;
  staleCta?: StaleAssessmentCta | null;
  onStaleCtaClick?: () => void;
};

const STALE_TOOLTIP_WIDTH = 156;
const STALE_TOOLTIP_HEIGHT = 108;
const STALE_HOVER_HIDE_MS = 350;

type StaleHoverState = {
  point: ChartPoint;
  anchorX: number;
  anchorY: number;
};

const computeStalePopoverStyle = (
  anchorX: number,
  anchorY: number,
  chartWidth: number,
  chartHeight: number
): React.CSSProperties => {
  const width = STALE_TOOLTIP_WIDTH;
  const height = STALE_TOOLTIP_HEIGHT + 12;
  const pad = 8;

  let left = anchorX + 12;
  if (left + width > chartWidth - pad) {
    left = anchorX - width - 12;
  }
  left = Math.max(pad, left);

  let top = anchorY + 8;
  if (top + height > chartHeight - pad) {
    top = anchorY - height - 6;
  }
  top = Math.max(pad, Math.min(top, chartHeight - height - pad));

  return { left, top, width };
};

const renderStaleTooltip = (
  prefix: "AL" | "RL",
  point: ChartPoint,
  currencyDays?: number,
  staleCta?: StaleAssessmentCta | null,
  onStaleCtaClick?: () => void
) => {
  const dateLabel = formatTrendTooltipDate(point.publishedDate);
  const windowLabel =
    currencyDays != null && currencyDays > 0 ? `${currencyDays}-day` : "";

  return (
    <div
      className="rounded-md border border-amber-200/90 bg-white px-2.5 py-2 shadow-md"
      style={{ width: STALE_TOOLTIP_WIDTH }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold tabular-nums text-amber-800">
          {prefix} {point.value}
        </span>
        <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-800">
          Stale
        </span>
      </div>
      {dateLabel ? (
        <div className="mt-1 text-[10px] leading-snug text-gray-600">
          Since {dateLabel}
        </div>
      ) : null}
      <div className="mt-1 text-[10px] leading-snug text-gray-500">
        {windowLabel
          ? `Past the ${windowLabel} currency window.`
          : "Past the currency window."}{" "}
        Start a new self-assessment.
      </div>
      {staleCta && onStaleCtaClick ? (
        <button
          type="button"
          className="mt-2 block w-full text-left text-[10px] font-semibold leading-tight text-violet-600 hover:text-violet-700 hover:underline"
          onClick={e => {
            e.stopPropagation();
            onStaleCtaClick();
          }}
        >
          {staleCta.label}
        </button>
      ) : null}
    </div>
  );
};

const TrendTooltipContent: React.FC<TrendTooltipContentProps> = ({
  active,
  payload,
  prefix,
  currencyDays,
  staleCta,
  onStaleCtaClick,
}) => {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  if (point.isUnknown) {
    return (
      <div className="rounded-md border border-gray-300 bg-gray-100 px-2.5 py-1.5 shadow-sm">
        <span className="text-sm font-bold text-gray-500">{prefix} ?</span>
        <div className="mt-0.5 text-[10px] leading-snug text-gray-500/80">
          No assessment data for {point.name}
        </div>
      </div>
    );
  }

  if (point.isStaleDot) {
    return null;
  }

  if (point.isToday) {
    const dateLabel = formatTrendTooltipDate(point.publishedDate);
    return (
      <div className="rounded-md border border-gray-300 bg-gray-100 px-2.5 py-1.5 shadow-sm">
        <span className="text-sm font-bold tabular-nums text-gray-500">
          {prefix} {point.value}
        </span>
        <div className="mt-0.5 text-[10px] leading-snug text-gray-500/80">
          Today{dateLabel ? ` · ${dateLabel}` : ""}
        </div>
      </div>
    );
  }

  if (point.isAxisOnly) return null;

  if (point.isTrailingEmpty) {
    return (
      <div className="rounded-md border border-gray-300 bg-gray-100 px-2.5 py-1.5 shadow-sm">
        <span className="text-sm font-bold tabular-nums text-gray-500">
          {prefix} {point.value}
        </span>
        <div className="mt-0.5 text-[10px] leading-snug text-gray-500/80">
          No new assessment for {point.name}
        </div>
      </div>
    );
  }

  if (point.value == null) return null;

  const dateLabel = formatTrendTooltipDate(point.publishedDate);
  const delta = point.trendDelta;
  const trendUp = delta != null && delta > 0;
  const trendDown = delta != null && delta < 0;

  return (
    <div className="rounded-md border border-amber-200 bg-[#fef9c3] px-2.5 py-1.5 shadow-sm">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-bold tabular-nums text-amber-700">
          {prefix} {point.value}
        </span>
        {trendUp ? (
          <span className="text-[10px] leading-none text-green-600" aria-hidden>
            ▲
          </span>
        ) : null}
        {trendDown ? (
          <span className="text-[10px] leading-none text-red-600" aria-hidden>
            ▼
          </span>
        ) : null}
      </div>
      {dateLabel ? (
        <div className="mt-0.5 text-[10px] leading-snug text-amber-800/80">
          {dateLabel}
        </div>
      ) : null}
      {point.assessmentName ? (
        <div className="mt-0.5 max-w-[10rem] truncate text-[10px] leading-snug text-amber-800/70">
          {point.assessmentName}
        </div>
      ) : null}
    </div>
  );
};

/** Always-visible level badge above the last chart point. */
const LastPointLevelLabel: React.FC<{
  cx?: number;
  cy?: number;
  prefix: "AL" | "RL";
  point: ChartPoint;
}> = ({ cx, cy, prefix, point }) => {
  if (cx == null || cy == null) return null;

  const delta = point.trendDelta;
  const trendUp = delta != null && delta > 0;
  const trendDown = delta != null && delta < 0;
  const label = `${prefix} ${point.value}`;
  // Approximate badge width for centering above the dot.
  const badgeW = 56 + (trendUp || trendDown ? 12 : 0);
  const badgeH = 20;

  return (
    <g transform={`translate(${cx - badgeW / 2}, ${cy - badgeH - 10})`}>
      <rect
        width={badgeW}
        height={badgeH}
        rx={4}
        ry={4}
        fill="#fef9c3"
        stroke="#fde68a"
        strokeWidth={1}
      />
      <text
        x={8}
        y={badgeH / 2 + 1}
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={700}
        fill="#b45309"
      >
        {label}
      </text>
      {trendUp ? (
        <polygon
          points={`${badgeW - 14},${badgeH / 2 + 4} ${badgeW - 8},${badgeH / 2 - 3} ${badgeW - 2},${badgeH / 2 + 4}`}
          fill="#16a34a"
        />
      ) : null}
      {trendDown ? (
        <polygon
          points={`${badgeW - 14},${badgeH / 2 - 3} ${badgeW - 8},${badgeH / 2 + 4} ${badgeW - 2},${badgeH / 2 - 3}`}
          fill="#dc2626"
        />
      ) : null}
    </g>
  );
};

type TrendLegendProps = {
  stroke: string;
  prefix: "AL" | "RL";
};

/** Explains chart markers: AL ?/RL ? / assessment available / no new assessment. */
const TrendLegend: React.FC<TrendLegendProps> = ({ stroke, prefix }) => (
  <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 px-0.5">
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: UNKNOWN_COLOR }}
        aria-hidden
      />
      <span className="text-[11px] leading-none text-gray-500">{prefix} ?</span>
    </div>
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2 bg-white"
        style={{ borderColor: stroke }}
        aria-hidden
      />
      <span className="text-[11px] leading-none text-gray-500">
        Assessment available
      </span>
    </div>
    <div className="flex items-center gap-1.5">
      <svg
        width="18"
        height="10"
        viewBox="0 0 18 10"
        className="shrink-0"
        aria-hidden
      >
        <line
          x1="0"
          y1="5"
          x2="18"
          y2="5"
          stroke={stroke}
          strokeWidth="2"
          strokeDasharray="3 2"
        />
      </svg>
      <span className="text-[11px] leading-none text-gray-500">
        Not available
      </span>
    </div>
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: STALE_COLOR }}
        aria-hidden
      />
      <span className="text-[11px] leading-none text-gray-500">Stale</span>
    </div>
  </div>
);

type TrendPanelProps = {
  title: string;
  prefix: "AL" | "RL";
  stroke: string;
  points: ChartPoint[];
  levelTicks: number[];
  levelCodeByNumber: Record<number, string>;
  period: TrendPeriod;
  onPeriodChange: (period: TrendPeriod) => void;
  isSample: boolean;
  comingSoon?: boolean;
  loading?: boolean;
  currencyDays?: number;
  staleCta?: StaleAssessmentCta | null;
  onStaleCtaClick?: () => void;
};

const TrendPanel: React.FC<TrendPanelProps> = ({
  title,
  prefix,
  stroke,
  points,
  levelTicks,
  levelCodeByNumber,
  period,
  onPeriodChange,
  isSample,
  comingSoon = false,
  loading,
  currencyDays,
  staleCta,
  onStaleCtaClick,
}) => {
  const showPlaceholder = isSample || comingSoon;
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const staleHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [staleHover, setStaleHover] = useState<StaleHoverState | null>(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  const clearStaleHideTimer = useCallback(() => {
    if (staleHideTimerRef.current) {
      clearTimeout(staleHideTimerRef.current);
      staleHideTimerRef.current = null;
    }
  }, []);

  const showStaleTooltip = useCallback(
    (point: ChartPoint, anchorX: number, anchorY: number) => {
      clearStaleHideTimer();
      setStaleHover({ point, anchorX, anchorY });
    },
    [clearStaleHideTimer]
  );

  const scheduleHideStaleTooltip = useCallback(() => {
    clearStaleHideTimer();
    staleHideTimerRef.current = setTimeout(() => {
      setStaleHover(null);
      staleHideTimerRef.current = null;
    }, STALE_HOVER_HIDE_MS);
  }, [clearStaleHideTimer]);

  useEffect(() => () => clearStaleHideTimer(), [clearStaleHideTimer]);

  useEffect(() => {
    setStaleHover(null);
  }, [points, period]);

  useEffect(() => {
    const node = chartAreaRef.current;
    if (!node) return;

    const updateSize = () => {
      setChartSize({
        width: node.clientWidth,
        height: node.clientHeight,
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, showPlaceholder]);

  const monthCount = useMemo(() => {
    const periodMonths = period === "3m" ? 3 : 6;
    const maxMonth = points.reduce(
      (max, point) => Math.max(max, point.monthIndex + 1),
      periodMonths
    );
    return Math.max(periodMonths, maxMonth);
  }, [period, points]);

  const monthTicks = useMemo(() => {
    const byMonth = new Map<number, string>();
    points.forEach(point => {
      if (!byMonth.has(point.monthIndex) && point.name) {
        byMonth.set(point.monthIndex, point.name);
      }
    });
    const ticks = Array.from({ length: monthCount }, (_, i) => ({
      x: i,
      name: byMonth.get(i) ?? "",
    })).filter(tick => tick.name);

    return ticks.sort((a, b) => a.x - b.x);
  }, [points, monthCount]);

  /**
   * Grey stroke: Unknown runs + bridges into/out of adjacent known points.
   * Teal stroke: known→known only (nulls at Unknown so paths never overlap).
   * Dotted: trailing carry after last known assessment.
   */
  const lineData = useMemo(() => {
    let lastKnownIdx = -1;
    for (let i = points.length - 1; i >= 0; i--) {
      const point = points[i];
      if (
        !point.isUnknown &&
        !point.isTrailingEmpty &&
        !point.isAxisOnly &&
        point.value != null
      ) {
        lastKnownIdx = i;
        break;
      }
    }

    return points.map((point, index) => {
      const isRealKnown =
        !point.isUnknown &&
        !point.isTrailingEmpty &&
        !point.isAxisOnly &&
        point.value != null;
      const prev = points[index - 1];
      const next = points[index + 1];
      const touchesUnknown =
        !!prev?.isUnknown || !!next?.isUnknown || !!point.isUnknown;

      let unknownStrokeValue: number | null = null;
      if (
        !point.isTrailingEmpty &&
        point.value != null &&
        (point.isUnknown || (isRealKnown && touchesUnknown))
      ) {
        unknownStrokeValue = point.value;
      }

      return {
        ...point,
        unknownStrokeValue,
        knownStrokeValue: isRealKnown ? point.value : null,
        carryStrokeValue:
          lastKnownIdx >= 0 &&
          index >= lastKnownIdx &&
          point.value != null &&
          !point.isUnknown
            ? point.value
            : null,
      };
    });
  }, [points]);

  const lastPlottedIndexInLineData = useMemo(() => {
    for (let i = lineData.length - 1; i >= 0; i--) {
      if (
        !lineData[i].isUnknown &&
        !lineData[i].isTrailingEmpty &&
        !lineData[i].isAxisOnly &&
        lineData[i].value != null
      ) {
        return i;
      }
    }
    return -1;
  }, [lineData]);

  return (
    <div className="relative flex min-w-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">
          {title}
        </div>
        <DropdownSelector
          label="Time Period"
          selected={period}
          options={PERIOD_OPTIONS}
          onSelect={value => onPeriodChange(value as TrendPeriod)}
        />
      </div>

      {loading ? (
        <div
          className="flex items-center justify-center text-sm text-gray-400"
          style={{ height: CHART_HEIGHT }}
        >
          Loading trend…
        </div>
      ) : (
        <div
          className={`relative w-full ${showPlaceholder ? "select-none" : ""}`}
          style={{ height: CHART_HEIGHT }}
        >
          <div
            ref={chartAreaRef}
            className={`relative h-full w-full ${
              showPlaceholder ? "pointer-events-none blur-[1px] opacity-80" : ""
            }`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={lineData}
                margin={{ top: 28, right: 28, left: 4, bottom: 8 }}
              >
                {!showPlaceholder ? (
                  <Tooltip
                    cursor={false}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{
                      pointerEvents: "none",
                      zIndex: 10,
                      outline: "none",
                    }}
                    content={
                      <TrendTooltipContent
                        prefix={prefix}
                        currencyDays={currencyDays}
                        staleCta={staleCta}
                        onStaleCtaClick={onStaleCtaClick}
                      />
                    }
                  />
                ) : null}
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={[
                    0,
                    Math.max(
                      monthCount - 1,
                      ...monthTicks.map(tick => tick.x),
                      0
                    ),
                  ]}
                  ticks={monthTicks.map(tick => tick.x)}
                  axisLine={false}
                  tickLine={false}
                  height={28}
                  tick={({ x, y, payload }) => {
                    if (x == null || y == null || payload == null) {
                      return <g />;
                    }
                    const label =
                      monthTicks.find(tick => tick.x === Number(payload.value))
                        ?.name ?? "";
                    if (!label) return <g />;
                    return (
                      <text
                        x={x}
                        y={y}
                        dy={12}
                        textAnchor="middle"
                        fontSize={11}
                        fill="#6b7280"
                      >
                        {label}
                      </text>
                    );
                  }}
                />
                <YAxis
                  type="number"
                  // Tight domain: padding below Unknown drew a second horizontal.
                  domain={[UNKNOWN_LEVEL, 5]}
                  ticks={levelTicks}
                  interval={0}
                  allowDecimals={false}
                  width={52}
                  axisLine={false}
                  tickLine={false}
                  tickMargin={6}
                  tick={({ x, y, payload }) => {
                    if (x == null || y == null || payload == null) {
                      return <g />;
                    }
                    const level = Number(payload.value);
                    if (level === UNKNOWN_LEVEL) {
                      return (
                        <text
                          x={x}
                          y={y}
                          dy={4}
                          textAnchor="end"
                          fontSize={11}
                          fontWeight={700}
                          fill={UNKNOWN_COLOR}
                        >
                          {prefix} ?
                        </text>
                      );
                    }
                    const color = LEVEL_LABEL_COLORS[level] ?? "#94a3b8";
                    return (
                      <text
                        x={x}
                        y={y}
                        dy={4}
                        textAnchor="end"
                        fontSize={11}
                        fontWeight={700}
                        fill={color}
                      >
                        {prefix} {level}
                      </text>
                    );
                  }}
                />
                {/* One dashed grid row per Y tick (including Unknown). */}
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />
                {/* Grey: Unknown months + bridge into first assessment */}
                <Line
                  type="linear"
                  dataKey="unknownStrokeValue"
                  stroke={UNKNOWN_COLOR}
                  strokeWidth={2}
                  connectNulls={false}
                  isAnimationActive={false}
                  activeDot={false}
                  dot={props => {
                    const { cx, cy, index, payload } = props;
                    const point = payload as ChartPoint;
                    if (
                      cx == null ||
                      cy == null ||
                      !point?.isUnknown ||
                      point?.value == null
                    ) {
                      return <g key={`unknown-dot-empty-${index}`} />;
                    }
                    return (
                      <g key={`unknown-dot-${point.key ?? index}`}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill="#ffffff"
                          stroke="none"
                        />
                        <circle
                          cx={cx}
                          cy={cy}
                          r={4}
                          fill={UNKNOWN_COLOR}
                          stroke={UNKNOWN_COLOR}
                          strokeWidth={2}
                        />
                      </g>
                    );
                  }}
                />
                {/* Teal solid: assessment → assessment */}
                <Line
                  type="linear"
                  dataKey="knownStrokeValue"
                  stroke={stroke}
                  strokeWidth={2}
                  connectNulls={false}
                  isAnimationActive={false}
                  legendType="none"
                  dot={props => {
                    const { cx, cy, index, payload } = props;
                    const point = payload as ChartPoint;
                    if (
                      cx == null ||
                      cy == null ||
                      point?.isUnknown ||
                      point?.isTrailingEmpty ||
                      point?.value == null
                    ) {
                      return <g key={`known-dot-empty-${index}`} />;
                    }
                    const isLast = index === lastPlottedIndexInLineData;
                    return (
                      <g key={`known-dot-${point.key ?? index}`}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill="#ffffff"
                          stroke="none"
                        />
                        <circle
                          cx={cx}
                          cy={cy}
                          r={4}
                          fill="#ffffff"
                          stroke={stroke}
                          strokeWidth={2}
                        />
                        {isLast && !showPlaceholder ? (
                          <LastPointLevelLabel
                            cx={cx}
                            cy={cy}
                            prefix={prefix}
                            point={point}
                          />
                        ) : null}
                      </g>
                    );
                  }}
                  activeDot={showPlaceholder ? false : { r: 5 }}
                />
                {/* Dotted carry only after last known assessment */}
                <Line
                  type="linear"
                  dataKey="carryStrokeValue"
                  stroke={stroke}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  connectNulls={false}
                  isAnimationActive={false}
                  legendType="none"
                  activeDot={false}
                  dot={props => {
                    const { cx, cy, index, payload } = props;
                    const point = payload as ChartPoint;
                    if (
                      cx == null ||
                      cy == null ||
                      !point?.isTrailingEmpty ||
                      point?.value == null
                    ) {
                      return <g key={`carry-dot-empty-${index}`} />;
                    }
                    if (point.isStaleDot) {
                      const staleDateLabel = formatStaleAxisDate(
                        point.publishedDate
                      );
                      return (
                        <g key={`carry-stale-dot-${point.key ?? index}`}>
                          {staleDateLabel ? (
                            <text
                              x={cx}
                              y={cy - 12}
                              textAnchor="middle"
                              fontSize={10}
                              fontWeight={600}
                              fill="#a16207"
                              pointerEvents="none"
                            >
                              {staleDateLabel}
                            </text>
                          ) : null}
                          <circle
                            cx={cx}
                            cy={cy}
                            r={14}
                            fill="transparent"
                            style={{ cursor: "pointer" }}
                            onMouseEnter={() => showStaleTooltip(point, cx, cy)}
                            onMouseLeave={scheduleHideStaleTooltip}
                          />
                          <circle
                            cx={cx}
                            cy={cy}
                            r={5}
                            fill="#ffffff"
                            stroke="none"
                            pointerEvents="none"
                          />
                          <circle
                            cx={cx}
                            cy={cy}
                            r={4}
                            fill={STALE_COLOR}
                            stroke={STALE_COLOR}
                            strokeWidth={2}
                            pointerEvents="none"
                          />
                        </g>
                      );
                    }
                    return (
                      <g key={`carry-dot-${point.key ?? index}`}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill="#ffffff"
                          stroke="none"
                        />
                        <circle
                          cx={cx}
                          cy={cy}
                          r={3.5}
                          fill="#ffffff"
                          stroke={stroke}
                          strokeWidth={2}
                          strokeDasharray="2 2"
                        />
                      </g>
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
            {staleHover && chartSize.width > 0 ? (
              <div
                className="absolute z-30"
                style={computeStalePopoverStyle(
                  staleHover.anchorX,
                  staleHover.anchorY,
                  chartSize.width,
                  chartSize.height
                )}
                onMouseEnter={clearStaleHideTimer}
                onMouseLeave={scheduleHideStaleTooltip}
              >
                <div className="-mt-4 pt-4">
                  {renderStaleTooltip(
                    prefix,
                    staleHover.point,
                    currencyDays,
                    staleCta,
                    onStaleCtaClick
                  )}
                </div>
              </div>
            ) : null}
          </div>
          {comingSoon ? (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-3">
              <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 shadow-sm">
                <span className="shrink-0 rounded bg-amber-200 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                  Coming soon
                </span>
                <span className="text-[12px] leading-snug text-amber-700">
                  The relationship trend graph appears here.
                </span>
              </div>
            </div>
          ) : null}
          {isSample && !comingSoon ? (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-3">
              <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 shadow-sm">
                <span className="shrink-0 rounded bg-amber-200 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                  Sample data
                </span>
                <span className="text-[12px] leading-snug text-amber-700">
                  Preview of how the {prefix} trend will look — real assessment
                  data will replace this.
                </span>
              </div>
            </div>
          ) : null}
        </div>
      )}
      {!loading ? <TrendLegend stroke={stroke} prefix={prefix} /> : null}
    </div>
  );
};

const AssessmentsTrendsSection: React.FC<AssessmentsTrendsSectionProps> = ({
  accountId,
  onOpenAssessments,
  embedded = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const staleAssessmentCta = useMemo(
    () => resolveStaleAssessmentCta(withOrg),
    [withOrg]
  );

  const handleStaleCtaClick = useCallback(() => {
    if (!staleAssessmentCta?.path) return;
    navigate(staleAssessmentCta.path);
  }, [navigate, staleAssessmentCta?.path]);

  const organizationLevels = useAppSelector(
    state => state.admin.organizationLevels
  );
  const {
    assessmentTrendline,
    assessmentTrendlineLoading,
    assessmentTrendlineError,
  } = useAppSelector(state => state.accountWorkspace);
  const selfAssessmentConfig = useAppSelector(
    state => state.assessment.selfAssessmentConfig
  );
  const currencyDays = resolveSelfAssessmentCurrencyDays(
    selfAssessmentConfig?.cadence_days,
    DEFAULT_CYCLE_CURRENCY_DAYS
  );

  const [alPeriod, setAlPeriod] = useState<TrendPeriod>("3m");
  const [rlPeriod, setRlPeriod] = useState<TrendPeriod>("3m");

  useEffect(() => {
    if (organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, organizationLevels.length]);

  useEffect(() => {
    dispatch(fetchSelfAssessmentConfig());
  }, [dispatch]);

  useEffect(() => {
    if (!accountId) return;
    const { from_date, to_date } = getTrendDateRange(alPeriod);
    dispatch(
      fetchAssessmentTrendline({
        accountId,
        from_date,
        to_date,
      })
    );
  }, [accountId, alPeriod, dispatch]);

  const positiveLevelTicks = useMemo(() => buildContinuousLevelTicks(), []);

  const levelTicks = useMemo(
    () => [UNKNOWN_LEVEL, ...positiveLevelTicks],
    [positiveLevelTicks]
  );

  const levelCodeByNumber = useMemo(
    () => buildLevelCodeByNumber(organizationLevels),
    [organizationLevels]
  );

  const alBuckets = useMemo(() => buildTrendMonthBuckets(alPeriod), [alPeriod]);

  const alRealPoints = useMemo(
    () =>
      buildAlChartPoints(
        alBuckets,
        assessmentTrendline?.points ?? [],
        levelCodeByNumber,
        currencyDays
      ),
    [alBuckets, assessmentTrendline?.points, levelCodeByNumber, currencyDays]
  );

  const alSamplePoints = useMemo(
    () => buildSamplePoints(positiveLevelTicks, alPeriod),
    [positiveLevelTicks, alPeriod]
  );

  const rlSamplePoints = useMemo(
    () => buildSamplePoints(positiveLevelTicks, rlPeriod),
    [positiveLevelTicks, rlPeriod]
  );

  const hasAlData =
    !assessmentTrendlineLoading &&
    !assessmentTrendlineError &&
    alRealPoints.some(
      point =>
        !point.isUnknown &&
        !point.isTrailingEmpty &&
        !point.isAxisOnly &&
        point.value != null
    );

  const alPoints = hasAlData ? alRealPoints : alSamplePoints;
  const showAlSample = !assessmentTrendlineLoading && !hasAlData;

  const charts = (
    <div className="flex flex-col gap-3 lg:flex-row">
      <TrendPanel
        title="Account Level Assessments (AL)"
        prefix="AL"
        stroke={AL_STROKE}
        points={alPoints}
        levelTicks={levelTicks}
        levelCodeByNumber={levelCodeByNumber}
        period={alPeriod}
        onPeriodChange={setAlPeriod}
        isSample={showAlSample}
        loading={assessmentTrendlineLoading}
        currencyDays={currencyDays}
        staleCta={staleAssessmentCta}
        onStaleCtaClick={handleStaleCtaClick}
      />
      <TrendPanel
        title="Relationship Level Assessments (RL - Aggregate)"
        prefix="RL"
        stroke={RL_STROKE}
        points={rlSamplePoints}
        levelTicks={levelTicks}
        levelCodeByNumber={levelCodeByNumber}
        period={rlPeriod}
        onPeriodChange={setRlPeriod}
        isSample={false}
        comingSoon
      />
    </div>
  );

  if (embedded) {
    return charts;
  }

  return (
    <SynopsisSectionCard
      icon={TrendingUp}
      title="AL & RL Assessments Trends"
      linkLabel="Relationships · Assessments"
      onLinkClick={onOpenAssessments}
    >
      {charts}
    </SynopsisSectionCard>
  );
};

export default AssessmentsTrendsSection;
